import { useState, useMemo } from 'react';
import { getActivityLogs } from '@/utils/activityLogStore';
import { demoUsers } from '@/mocks/demoData';

const ACTION_TYPES = ['all', 'Check-in', 'Check-out', 'Payment', 'Booking', 'Canteen', 'Shift', 'Stock', 'Refund', 'Expense', 'Housekeeping'];

export default function ReceptionLogs() {
  const [filterUser, setFilterUser] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const filteredLogs = useMemo(() => {
    return getActivityLogs().filter((log) => {
      const matchUser = filterUser === 'all' || log.user_id === filterUser;
      const matchAction = filterAction === 'all' || log.action.includes(filterAction);
      const matchSearch = !searchQuery ||
        log.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.entity_id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchUser && matchAction && matchSearch;
    });
  }, [filterUser, filterAction, searchQuery]);

  // ... existing code ...
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filteredLogs.length / pageSize);

  const getActionIcon = (action: string) => {
    if (action.includes('Check-in')) return 'ri-login-box-line text-green-500';
    if (action.includes('Check-out')) return 'ri-logout-box-line text-red-500';
    if (action.includes('Payment')) return 'ri-money-rupee-circle-line text-gold-500';
    if (action.includes('Canteen')) return 'ri-restaurant-line text-orange-500';
    if (action.includes('Booking')) return 'ri-calendar-check-line text-navy-500';
    if (action.includes('Shift')) return 'ri-time-line text-navy-400';
    if (action.includes('Stock')) return 'ri-box-3-line text-blue-500';
    if (action.includes('Refund')) return 'ri-refund-2-line text-purple-500';
    if (action.includes('Expense')) return 'ri-money-dollar-circle-line text-red-500';
    if (action.includes('Housekeeping')) return 'ri-brush-line text-teal-500';
    return 'ri-file-list-line text-navy-400';
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Activity Logs</h1>
        <p className="text-navy-500 mt-1">Audit trail of all hotel operations and user actions.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
              placeholder="Search user, action, entity..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
            value={filterUser}
            onChange={(e) => { setFilterUser(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Users</option>
            {demoUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
            value={filterAction}
            onChange={(e) => { setFilterAction(e.target.value); setCurrentPage(1); }}
          >
            {ACTION_TYPES.map((a) => (
              <option key={a} value={a}>{a === 'all' ? 'All Actions' : a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700 w-10"></th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Action</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">User</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Entity</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Details</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Time</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.map((log) => (
                <tr key={log.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <i className={`${getActionIcon(log.action)} text-base`} />
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-medium text-navy-800">{log.action}</span>
                    <div className="text-xs text-navy-400">{log.entity_type} #{log.entity_id.slice(-6)}</div>
                  </td>
                  <td className="px-5 py-3.5 text-navy-600">{log.user_name}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex px-2 py-1 rounded-md bg-navy-50 text-navy-600 text-xs font-medium capitalize">
                      {log.entity_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-navy-500 text-xs max-w-xs truncate">
                    {log.details && (log.details as { guest?: string; room?: string; amount?: number; method?: string; receipt?: string }).guest
                      ? `Guest: ${(log.details as { guest?: string }).guest}`
                      : log.details && (log.details as { amount?: number }).amount
                      ? `Amount: Rs. ${(log.details as { amount?: number }).amount?.toLocaleString('en-IN')}`
                      : log.details && (log.details as { category?: string }).category
                      ? `${(log.details as { category?: string }).category}`
                      : JSON.stringify(log.details).slice(0, 60)}
                  </td>
                  <td className="px-5 py-3.5 text-right text-navy-400 text-xs whitespace-nowrap">
                    {new Date(log.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    <br />
                    {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
              {paginatedLogs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-navy-400">
                    <i className="ri-search-line text-3xl mb-2 block" />
                    No logs found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-navy-100 flex items-center justify-between">
            <div className="text-sm text-navy-500">
              Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredLogs.length)} of {filteredLogs.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 rounded-lg border border-navy-200 flex items-center justify-center text-navy-600 disabled:text-navy-300 disabled:cursor-not-allowed hover:bg-navy-50 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer ${
                    currentPage === i + 1
                      ? 'bg-navy-700 text-white'
                      : 'border border-navy-200 text-navy-600 hover:bg-navy-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-lg border border-navy-200 flex items-center justify-center text-navy-600 disabled:text-navy-300 disabled:cursor-not-allowed hover:bg-navy-50 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}